import { ApiError } from "@/lib/api";
import { useReportErrorScreen } from "@/lib/client-error-report";
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Error of a single page, shown in place of that page only: the sidebar and
 * whatever else the layout holds stay. A lost connection or a restarting
 * server retries by itself, so a deploy passes without anyone clicking.
 */

const CHUNK_ERROR =
  /dynamically imported module|importing a module script failed|error loading dynamically imported|unable to preload/i;
const NETWORK_ERROR = /failed to fetch|networkerror|load failed|network request failed|timed out/i;

export function isStaleBuildError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

function isTransient(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 502 && error.status <= 504;
  return error instanceof TypeError && NETWORK_ERROR.test(error.message);
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const stale = isStaleBuildError(error);
  const transient = !stale && isTransient(error);
  const [retrying, setRetrying] = useState(false);
  useReportErrorScreen("page", error.message, transient);

  const retry = async () => {
    setRetrying(true);
    try {
      reset();
      await router.invalidate();
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (!transient) return;
    const id = setInterval(() => {
      reset();
      void router.invalidate().catch(() => {});
    }, 5_000);
    return () => clearInterval(id);
  }, [transient, reset, router]);

  return (
    <div className="flex justify-center py-16">
      <div className="max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
        <h1 className="font-heading text-xl font-bold">
          {stale
            ? t("routeError.updated")
            : transient
              ? t("routeError.reconnecting")
              : t("routeError.pageFailed")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {stale
            ? t("routeError.updatedHint")
            : transient
              ? t("routeError.reconnectingHint")
              : error.message}
        </p>
        {transient && (
          <p className="mt-2 break-all text-xs text-muted-foreground/70">{error.message}</p>
        )}
        <button
          type="button"
          onClick={() => (stale ? window.location.reload() : void retry())}
          disabled={retrying}
          className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {stale
            ? t("routeError.reload")
            : retrying
              ? t("routeError.retrying")
              : t("routeError.retry")}
        </button>
      </div>
    </div>
  );
}
